import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { asyncVoid, noop } from '../utils/helper';
import { RestaurantContext, Severity } from './RestaurantContext';
import PincodeModal from '../Components/Organisms/PincodeModal';
import useCollection from 'CustomHooks/useCollection';
import { SignatureFailure, signatureApi, signatureFailure } from 'api/signatures';

/** A waiter signature as listed (restaurants/{id}/signatures): never a PIN or verifier, only whether it has one. */
export type Signature = {
  signature: string;
  name: string;
  hasPin?: boolean;
  createdAt: number;
};

/**
 * Whether a stored signature asks for a PIN: `hasPin` once the restaurant is prepared
 * (the public `code` is then only a marker), else any non-empty legacy `code` — never
 * read as "no PIN" by mistake. The stored value itself is not passed on.
 */
export const listedSignature = (stored: Record<string, any>): Signature => ({
  signature: String(stored.signature ?? ''),
  name: String(stored.name ?? ''),
  createdAt: Number(stored.createdAt ?? 0),
  hasPin: typeof stored.hasPin === 'boolean' ? stored.hasPin : !!stored.code,
});

/** A signature to create; the PIN goes to the server only. */
export type NewSignature = { signature: string; name: string; pin?: string | null };

export type PinCodeModalState = {
  open: boolean;
  error?: null | string;
  /** After the server accepted the PIN; gets it for a following change that needs the current PIN. */
  onValidCode?: (pin: string) => Promise<void> | void;
  type?: 'validate' | 'new' | 'edit';
  signatureModalData?: Signature;
};

type SignatureChange = { name?: string; pin?: string | null; currentPin?: string };

/**
 * Identity of the PIN prompt as it was when an answer was requested. Every change of the
 * prompt (opened, switched type, closed) and of the restaurant makes a new one; an answer
 * that arrives for an older one is dropped — no callback, no reopening, no write.
 */
export type PromptToken = { readonly generation: number };

type SignatureContextType = {
  signatures: Signature[];
  isLoading: boolean;
  error?: string | null;
  addSignature: (s: NewSignature) => Promise<boolean>;
  changeSignature: (signature: string, change: SignatureChange) => Promise<boolean>;
  deleteSignature: (sId: string) => Promise<void>;
  /** null when the server accepted the PIN; otherwise its refusal (wrong, locked, not set up). */
  verifyPin: (signature: string, pin: string) => Promise<SignatureFailure | null>;
  pincodeModalData: PinCodeModalState;
  setPincodeModalData: (val: PinCodeModalState) => void;
  addSignatureWithPincode: (s: NewSignature) => void;
  validateSignature: (s: string) => boolean;
  promptToken: () => PromptToken;
  isCurrentPrompt: (token: PromptToken) => boolean;
};

interface ContextProps {
  restaurantId: null | string;
  children?: React.ReactNode;
}

export const SignatureContext = createContext<SignatureContextType>({
  signatures: [],
  isLoading: false,
  error: null,
  addSignature: async () => false,
  changeSignature: async () => false,
  deleteSignature: asyncVoid,
  verifyPin: async () => null,
  pincodeModalData: { open: false },
  setPincodeModalData: noop,
  addSignatureWithPincode: noop,
  validateSignature: () => false,
  promptToken: () => ({ generation: 0 }),
  isCurrentPrompt: () => false,
});

const SignatureContextProvider = ({
  restaurantId,
  children,
}: ContextProps) => {
  const { newToast } = useContext(RestaurantContext);

  const [stored] = useCollection<Record<string, any>>(
    `restaurants/${restaurantId}/signatures`
  );
  const list = useMemo(() => stored.data.map(listedSignature), [stored.data]);

  const [error, seterror] = useState<string | null>(null);
  const [pincodeModalData, setPincodeModalState] = useState<PinCodeModalState>({
    open: false,
  });

  // The restaurant and prompt an answer belongs to (see PromptToken). Updated while
  // rendering, so an answer arriving right after a restaurant switch is already stale.
  const prompt = useRef<PromptToken>({ generation: 0 });
  const currentRestaurant = useRef(restaurantId);
  if (currentRestaurant.current !== restaurantId) {
    currentRestaurant.current = restaurantId;
    prompt.current = { generation: prompt.current.generation + 1 };
  }
  const setPincodeModalData = useCallback((value: PinCodeModalState) => {
    prompt.current = { generation: prompt.current.generation + 1 };
    setPincodeModalState(value);
  }, []);
  const promptToken = useCallback(() => prompt.current, []);
  const isCurrentPrompt = useCallback((token: PromptToken) => token === prompt.current, []);

  // Another restaurant: an open PIN prompt (and its pending action) belongs to the previous one.
  useEffect(() => {
    setPincodeModalState({ open: false });
    seterror(null);
  }, [restaurantId]);

  /**
   * After a write: its answer only shows for the restaurant (and closes only the prompt)
   * it was started for. The write itself was valid for that restaurant.
   */
  const settle = (forRestaurant: string, token: PromptToken) => ({
    stale: () => currentRestaurant.current !== forRestaurant,
    closePrompt: () => {
      if (prompt.current === token) setPincodeModalData({ open: false });
    },
  });

  function validateSignature(signature: string): boolean {
    return list.some((s) => s.signature === signature);
  }

  const refused = (e: unknown) => {
    const failure = signatureFailure(e);
    seterror(failure.message);
    newToast(failure.message, Severity.WARNING);
    return failure;
  };

  const addSignature = async (signature: NewSignature) => {
    if (!restaurantId) return false;
    const after = settle(restaurantId, prompt.current);
    try {
      await signatureApi.create(restaurantId, {
        signature: signature.signature,
        name: signature.name,
        pin: signature.pin || null,
      });
      if (after.stale()) return false;
      after.closePrompt();
      newToast('Signature added successfully.', Severity.SUCCESS, 'settings');
      return true;
    } catch (e) {
      if (!after.stale()) refused(e);
      return false;
    }
  };

  const changeSignature = async (signature: string, change: SignatureChange) => {
    if (!restaurantId) return false;
    const after = settle(restaurantId, prompt.current);
    try {
      await signatureApi.change(restaurantId, signature, change);
      if (after.stale()) return false;
      after.closePrompt();
      newToast('Signature edited successfully.', Severity.SUCCESS, 'settings');
      return true;
    } catch (e) {
      if (!after.stale()) refused(e);
      return false;
    }
  };

  const verifyPin = async (signature: string, pin: string) => {
    if (!restaurantId) return null;
    try {
      await signatureApi.verify(restaurantId, signature, pin);
      return null;
    } catch (e) {
      return signatureFailure(e);
    }
  };

  function addSignatureWithPincode(currentData: NewSignature) {
    setPincodeModalData({
      type: 'new',
      open: true,
      signatureModalData: { signature: currentData.signature, name: currentData.name, createdAt: Date.now() },
    });
  }

  const deleteSignature = async (signatureId: string) => {
    if (!restaurantId) return;
    const after = settle(restaurantId, prompt.current);
    try {
      await signatureApi.remove(restaurantId, signatureId);
      if (!after.stale()) newToast('Signature deleted successfully.', Severity.SUCCESS, 'settings');
    } catch (e) {
      if (!after.stale()) refused(e);
    }
  };

  return (
    <SignatureContext.Provider
      value={{
        signatures: list,
        isLoading: stored.loading,
        error,
        addSignature,
        changeSignature,
        deleteSignature,
        verifyPin,
        pincodeModalData,
        setPincodeModalData,
        addSignatureWithPincode,
        validateSignature,
        promptToken,
        isCurrentPrompt,
      }}
    >
      <PincodeModal
        open={pincodeModalData.open}
        type={pincodeModalData.type}
        signatureModalData={pincodeModalData.signatureModalData as Signature}
        onClose={() =>
          setPincodeModalData({ open: false, signatureModalData: undefined })
        }
      />
      {children}
    </SignatureContext.Provider>
  );
};

export default SignatureContextProvider;
